/**
 * What the application stores.
 *
 * Every entity carries `updatedAt` so that records can be merged, sorted and
 * pruned without a separate index, and every identifier is a string so that
 * stores stay uniform and nothing has to know an entity's key type.
 */

/**
 * Which of the two worlds a piece of data belongs to.
 *
 * These are not labels on records inside one database. Each profile has its
 * own database file, so a shared query has no path to a private record even if
 * someone writes the query wrongly. Isolation is structural, not filtered.
 */
export const PROFILES = ['shared', 'private'] as const;
export type ProfileId = (typeof PROFILES)[number];

/** A film, episode or clip the application knows about. */
export interface MediaItem {
  readonly id: string;
  readonly title: string;
  /** Where the media came from. */
  readonly source: MediaSource;
  readonly durationMs: number | null;
  /** Fingerprint used to confirm two people hold the same file. */
  readonly fingerprint: string | null;
  readonly sizeBytes: number | null;
  /** Object URL or data URI for the poster, if one has been generated. */
  readonly posterKey: string | null;
  readonly addedAt: number;
  readonly updatedAt: number;
}

export type MediaSource =
  | { readonly kind: 'local-file'; readonly fileName: string; readonly handleKey: string | null }
  | { readonly kind: 'archive'; readonly identifier: string; readonly url: string }
  | { readonly kind: 'url'; readonly url: string };

/** One viewing session. Append only, so the Activity feed has real history. */
export interface HistoryEntry {
  readonly id: string;
  readonly mediaId: string;
  readonly startedAt: number;
  readonly endedAt: number | null;
  readonly fromPositionMs: number;
  readonly toPositionMs: number;
  /** Set when the session took place inside a room. */
  readonly roomCode: string | null;
  readonly updatedAt: number;
}

/**
 * Where to pick a film back up.
 *
 * Separate from history because it is a single current value per item rather
 * than a log, and Continue Watching reads it constantly.
 */
export interface ResumePoint {
  /** The media id, used directly as the key so a lookup is a single get. */
  readonly id: string;
  readonly positionMs: number;
  readonly durationMs: number | null;
  readonly finished: boolean;
  readonly updatedAt: number;
}

export interface Favorite {
  readonly id: string;
  readonly mediaId: string;
  readonly updatedAt: number;
}

/** A marked moment inside a film. */
export interface Bookmark {
  readonly id: string;
  readonly mediaId: string;
  readonly positionMs: number;
  readonly label: string;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface Collection {
  readonly id: string;
  readonly name: string;
  readonly mediaIds: readonly string[];
  readonly updatedAt: number;
}

/** Anything with an id and a modification time, which is every entity above. */
export interface StoredRecord {
  readonly id: string;
  readonly updatedAt: number;
}

/** The stores held in each profile database. */
export const STORE_NAMES = [
  'media',
  'history',
  'resume',
  'favorites',
  'bookmarks',
  'collections',
] as const;
export type StoreName = (typeof STORE_NAMES)[number];

/**
 * Treat a resume point as finished once this close to the end.
 *
 * Credits and logos mean the last stretch of a file is rarely watched, so a
 * strict comparison against the duration would leave films sitting in Continue
 * Watching forever at 99%.
 */
export const FINISHED_THRESHOLD = 0.97;

export function isEffectivelyFinished(positionMs: number, durationMs: number | null): boolean {
  if (durationMs === null || durationMs <= 0) return false;
  return positionMs / durationMs >= FINISHED_THRESHOLD;
}
